import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { AnyValue } from "@/lib/rowTypes";

export type Phase =
  | "intro" | "setup" | "tutorial" | "marketplace"
  | "ownership" | "expansion" | "advanced" | "summary" | "completed";

export interface OwnedProperty {
  property_key: string;
  property_name: string;
  city: string;
  property_type: string;
  purchase_price: number;
  current_value: number;
  mortgage_balance: number;
  monthly_mortgage: number;
  rent: number;
  current_rent: number;
  condition_score: number;
  tenant_quality: number;
  occupied: boolean;
  vacancy_months: number;
  total_rent_collected: number;
  total_repairs_paid: number;
  appreciation_gained: number;
  down_payment_pct: number;
  interest_rate: number;
  mortgage_years: number;
  hoa_monthly: number;
  renovation_spent: number;
  has_property_manager: boolean;
  manager_fee_pct: number;
}

export interface SimState {
  id: string;
  user_id: string;
  status: string;
  current_phase: Phase;
  starting_capital: number;
  available_cash: number;
  risk_style: string;
  selected_market: string;
  current_round: number;
  max_rounds: number;
  market_cycle: string;
  portfolio: OwnedProperty[];
  event_history: AnyValue[];
  achievements: string[];
  reserve_balance: number;
  total_rental_income: number;
  total_repair_costs: number;
  total_appreciation: number;
  metrics: Record<string, AnyValue>;
}

export interface InvestmentProperty {
  id: string;
  property_key: string;
  property_name: string;
  city: string;
  property_type: string;
  purchase_price: number;
  estimated_rent: number;
  condition_score: string;
  appreciation_rating: string;
  maintenance_risk: string;
  tenant_demand: string;
  risk_level: string;
  hoa_monthly: number;
  notes: string;
  display_order: number;
}

export const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

type FunctionErrorDetails = {
  error?: string;
  needed?: number;
  available?: number;
  status?: number;
};

const extractFunctionErrorDetails = async (error: AnyValue): Promise<FunctionErrorDetails> => {
  const response = error?.context;

  if (response && typeof response.clone === "function") {
    const payload = await response.clone().json().catch(() => null);

    return {
      status: response.status,
      error: payload?.error || error?.message || "Request failed",
      needed: typeof payload?.needed === "number" ? payload.needed : undefined,
      available: typeof payload?.available === "number" ? payload.available : undefined,
    };
  }

  return {
    status: typeof response?.status === "number" ? response.status : undefined,
    error: error?.message || "Request failed",
  };
};

const buildFundsMessage = (details?: FunctionErrorDetails, fallback?: string) => {
  if (!details?.error) return fallback || "Request failed.";

  if (details.error.toLowerCase().includes("insufficient funds")) {
    const neededText = typeof details.needed === "number" ? fmt(details.needed) : null;
    const availableText = typeof details.available === "number" ? fmt(details.available) : null;

    if (neededText && availableText) {
      return `You need ${neededText} and only have ${availableText}.`;
    }

    if (neededText) {
      return `You need ${neededText} to continue.`;
    }

    return fallback || "You don't have enough cash for that move yet.";
  }

  return details.error;
};

export function useREInvestingSim() {
  const [sim, setSim] = useState<SimState | null>(null);
  const simRef = useRef<SimState | null>(null);
  const updateSim = useCallback((newSim: SimState | null) => {
    simRef.current = newSim;
    setSim(newSim);
  }, []);
  const [properties, setProperties] = useState<InvestmentProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState<AnyValue>(null);
  const [pendingEvent, setPendingEvent] = useState<AnyValue>(null);

  const invoke = useCallback(async (body: Record<string, AnyValue>) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) throw new Error("Not authenticated");

    const res = await supabase.functions.invoke("re-investing-engine", {
      body,
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (res.error) {
      const details = await extractFunctionErrorDetails(res.error);
      const enrichedError = new Error(details.error || res.error.message || "Request failed");
      (enrichedError as Error & { details?: FunctionErrorDetails }).details = details;
      throw enrichedError;
    }

    return res.data;
  }, []);

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      let simData: AnyValue = null;

      try {
        const stateRes = await invoke({ action: "get_state" });
        simData = stateRes?.sim || null;
      } catch {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session) {
          const { data } = await supabase
            .from("re_investing_simulations")
            .select("*")
            .eq("user_id", session.user.id)
            .eq("status", "active")
            .order("created_at", { ascending: false })
            .limit(1)
            .single();

          simData = data;
        }
      }

      const { data: props } = await supabase
        .from("fictional_investment_properties")
        .select("*")
        .order("display_order");

      if (simData) updateSim(simData);
      if (props) setProperties(props);
    } catch (err: unknown) {
      console.error("loadState error:", err);
    } finally {
      setLoading(false);
    }
  }, [invoke, updateSim]);

  const startSim = useCallback(
    async (starting_capital: number, risk_style: string, selected_market: string) => {
      setProcessing(true);
      try {
        const res = await invoke({ action: "start", starting_capital, risk_style, selected_market });
        if (res?.sim) {
          console.log("[RE-Investing] startSim success, phase:", res.sim.current_phase);
          updateSim(res.sim);
          return res;
        }
        console.warn("[RE-Investing] startSim: no sim in response, using fallback");
      } catch (err: unknown) {
        console.error("[RE-Investing] startSim error:", err);
        toast.error("Connection issue — starting in offline mode.");
      }

      const fallback: SimState = {
        id: crypto.randomUUID(),
        user_id: "",
        status: "active",
        current_phase: "tutorial" as Phase,
        starting_capital,
        available_cash: starting_capital,
        risk_style,
        selected_market,
        current_round: 0,
        max_rounds: 12,
        market_cycle: "stable",
        portfolio: [],
        event_history: [],
        achievements: [],
        reserve_balance: 0,
        total_rental_income: 0,
        total_repair_costs: 0,
        total_appreciation: 0,
        metrics: {},
      };

      updateSim(fallback);
      setProcessing(false);
      return { sim: fallback };
    },
    [invoke, updateSim],
  );

  const buyProperty = useCallback(
    async (property_key: string, down_payment_pct: number, interest_rate: number, mortgage_years: number) => {
      const currentSim = simRef.current;
      if (!currentSim) return;

      setProcessing(true);
      try {
        const res = await invoke({
          action: "buy",
          sim_id: currentSim.id,
          property_key,
          down_payment_pct,
          interest_rate,
          mortgage_years,
        });

        if (res?.sim) updateSim(res.sim);
        setLastResult(res?.purchase_summary);
        return res;
      } catch (err: unknown) {
        const details = (err as AnyValue)?.details as FunctionErrorDetails | undefined;
        toast.error(buildFundsMessage(details, "You don't have enough cash to buy this property."));
        return { error: details || err };
      } finally {
        setProcessing(false);
      }
    },
    [invoke, updateSim],
  );

  const advanceRound = useCallback(async () => {
    const currentSim = simRef.current;
    if (!currentSim) return;

    setProcessing(true);
    try {
      const res = await invoke({ action: "advance", sim_id: currentSim.id });
      if (res?.sim) updateSim(res.sim);
      if (res?.round_summary?.triggered_event) {
        setPendingEvent(res.round_summary.triggered_event);
      }
      setLastResult(res?.round_summary);
      return res;
    } catch (err: unknown) {
      toast.error((err as AnyValue)?.details?.error || "Unable to advance the simulation right now.");
      return { error: (err as AnyValue)?.details || err };
    } finally {
      setProcessing(false);
    }
  }, [invoke, updateSim]);

  const resolveEvent = useCallback(
    async (event_id: string, choice_index: number) => {
      const currentSim = simRef.current;
      if (!currentSim) return;

      setProcessing(true);
      try {
        const res = await invoke({ action: "resolve_event", sim_id: currentSim.id, event_id, choice_index });
        if (res?.sim) updateSim(res.sim);
        setPendingEvent(null);
        setLastResult(res?.outcome);
        return res;
      } catch (err: unknown) {
        toast.error((err as AnyValue)?.details?.error || "Unable to resolve this event right now.");
        return { error: (err as AnyValue)?.details || err };
      } finally {
        setProcessing(false);
      }
    },
    [invoke, updateSim],
  );

  const propertyAction = useCallback(
    async (property_key: string, actionType: string) => {
      const currentSim = simRef.current;
      if (!currentSim) return;

      setProcessing(true);
      try {
        const res = await invoke({
          action: "property_action",
          sim_id: currentSim.id,
          property_key,
          property_action: actionType,
        });

        if (res?.sim) updateSim(res.sim);
        setLastResult(res?.result);
        return res;
      } catch (err: unknown) {
        const details = (err as AnyValue)?.details as FunctionErrorDetails | undefined;
        toast.error(buildFundsMessage(details, `Unable to ${actionType.replace(/_/g, " ")} right now.`));
        return { error: details || err };
      } finally {
        setProcessing(false);
      }
    },
    [invoke, updateSim],
  );

  const setPhase = useCallback(
    async (phase: Phase) => {
      const currentSim = simRef.current;
      if (!currentSim) return;

      try {
        const res = await invoke({ action: "set_phase", sim_id: currentSim.id, phase });
        if (res?.sim) {
          updateSim(res.sim);
          return;
        }
      } catch {
        // Fall through to local update
      }

      updateSim(simRef.current ? { ...simRef.current, current_phase: phase } : null);
    },
    [invoke, updateSim],
  );

  const totalPortfolioValue = sim?.portfolio?.reduce((sum, p) => sum + p.current_value, 0) || 0;
  const totalMortgageDebt = sim?.portfolio?.reduce((sum, p) => sum + p.mortgage_balance, 0) || 0;
  const totalEquity = totalPortfolioValue - totalMortgageDebt;
  const netWorth = (sim?.available_cash || 0) + totalEquity;
  const monthlyCashFlow =
    sim?.portfolio?.reduce((sum, p) => {
      if (!p.occupied) return sum - p.monthly_mortgage - p.hoa_monthly;
      return sum + p.current_rent - p.monthly_mortgage - p.hoa_monthly;
    }, 0) || 0;

  return {
    sim,
    properties,
    loading,
    processing,
    lastResult,
    pendingEvent,
    loadState,
    startSim,
    buyProperty,
    advanceRound,
    resolveEvent,
    propertyAction,
    setPhase,
    setPendingEvent,
    setLastResult,
    totalPortfolioValue,
    totalMortgageDebt,
    totalEquity,
    netWorth,
    monthlyCashFlow,
  };
}
